import { useState, useEffect } from 'react';
import { Clock, DollarSign, CheckCircle, XCircle, Calendar, User, Tag, Gift } from 'lucide-react';
import { axiosInstance } from '../lib/api-client';
import { toast } from 'sonner';

interface PromotionalOrder {
  _id: string;
  userId: {
    firstName: string;
    lastName: string;
    email: string;
  };
  planId: {
    name: string;
    price: number;
  };
  discountedPrice: number;
  originalPrice: number;
  discountPercentage: number;
  extraContacts: number;
  extraValidity: number;
  status: 'pending' | 'completed' | 'expired' | 'cancelled';
  campaignName?: string;
  notes?: string;
  commissionEarned: number;
  commissionPercentage?: number; // Added commission rate
  validUntil: string;
  createdAt: string;
  viewCount?: number;
}

export default function PromotionalOrdersList() {
  const [orders, setOrders] = useState<PromotionalOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'pending' | 'completed' | 'expired'>('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchOrders();
  }, [filter, page]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const params: any = { page, limit: 10 }; // Changed from 20 to 10
      if (filter !== 'all') {
        params.status = filter;
      }

      const response = await axiosInstance.get('/promotional-orders/my-orders', { params });

      if (response.data.success) {
        setOrders(response.data.data.orders || []);
        setTotalPages(response.data.data.pagination?.totalPages || 1);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch promotional orders');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      pending: (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <Clock className="w-3 h-3 mr-1" />
          Pending
        </span>
      ),
      completed: (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle className="w-3 h-3 mr-1" />
          Completed
        </span>
      ),
      expired: (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          <XCircle className="w-3 h-3 mr-1" />
          Expired
        </span>
      ),
      cancelled: (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <XCircle className="w-3 h-3 mr-1" />
          Cancelled
        </span>
      )
    };
    return badges[status as keyof typeof badges] || null;
  };

  if (loading && orders.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin w-8 h-8 border-4 border-rose-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        {['all', 'pending', 'completed', 'expired'].map((status) => (
          <button
            key={status}
            onClick={() => {
              setFilter(status as typeof filter);
              setPage(1);
            }}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === status
                ? 'bg-rose-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </button>
        ))}
      </div>

      {/* Orders List */}
      {orders.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
          <Gift className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">No promotional orders found</p>
          <p className="text-sm text-gray-500 mt-1">Create promotional offers to see them here</p>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((order) => (
            <div
              key={order._id}
              className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex flex-col gap-4">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <div className="bg-rose-100 rounded-full p-2">
                      <User className="w-5 h-5 text-rose-600" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900">
                        {order.userId.firstName} {order.userId.lastName}
                      </h4>
                      <p className="text-sm text-gray-600">{order.userId.email}</p>
                      {order.campaignName && (
                        <div className="flex items-center gap-1 mt-1">
                          <Tag className="w-3 h-3 text-purple-600" />
                          <span className="text-xs text-purple-600 font-medium">{order.campaignName}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  {getStatusBadge(order.status)}
                </div>

                {/* Pricing & Details */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500 mb-1">Plan</p>
                    <p className="font-semibold text-gray-900">{order.planId.name}</p>
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500 mb-1">Original Price</p>
                    <p className="font-semibold text-gray-900">₹{order.originalPrice.toLocaleString()}</p>
                  </div>
                  
                  <div className="bg-green-50 rounded-lg p-3">
                    <p className="text-xs text-green-600 mb-1">Discounted Price</p>
                    <p className="font-semibold text-green-700">₹{order.discountedPrice.toLocaleString()}</p>
                    <p className="text-xs text-green-600 mt-0.5">{order.discountPercentage.toFixed(0)}% OFF</p>
                  </div>
                  
                  <div className="bg-blue-50 rounded-lg p-3">
                    <p className="text-xs text-blue-600 mb-1">Commission</p>
                    <p className="font-semibold text-blue-700">₹{order.commissionEarned.toLocaleString()}</p>
                    {order.commissionPercentage && (
                      <p className="text-xs text-blue-600 mt-0.5">{order.commissionPercentage}% of ₹{order.discountedPrice.toLocaleString()}</p>
                    )}
                  </div>
                </div>

                {/* Bonus Features */}
                {(order.extraContacts > 0 || order.extraValidity > 0) && (
                  <div className="flex flex-wrap gap-2">
                    {order.extraContacts > 0 && (
                      <div className="inline-flex items-center gap-1 px-3 py-1 bg-purple-50 border border-purple-200 rounded-full text-xs text-purple-700">
                        <Gift className="w-3 h-3" />
                        +{order.extraContacts} Contacts
                      </div>
                    )}
                    {order.extraValidity > 0 && (
                      <div className="inline-flex items-center gap-1 px-3 py-1 bg-purple-50 border border-purple-200 rounded-full text-xs text-purple-700">
                        <Calendar className="w-3 h-3" />
                        +{order.extraValidity} Days
                      </div>
                    )}
                  </div>
                )}

                {/* Footer */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pt-3 border-t border-gray-100">
                  <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                    <span>Created: {formatDate(order.createdAt)}</span>
                    {order.status === 'pending' && (
                      <span>Expires: {formatDate(order.validUntil)}</span>
                    )}
                    {order.viewCount !== undefined && (
                      <span>Views: {order.viewCount}</span>
                    )}
                  </div>
                  
                  {order.notes && (
                    <button
                      className="text-xs text-gray-600 hover:text-gray-900 underline"
                      onClick={() => toast.info(order.notes, { duration: 5000 })}
                    >
                      View Notes
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <span className="text-sm text-gray-600">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}